/**
 * Queries y mutations de administración de usuarios.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export interface AdminUser {
  id: string;
  email: string;
  nombre: string;
  avatarUrl?: string | null;
  rol: 'admin' | 'lector';
  activo: boolean;
  tienePassword: boolean;
  createdAt: number;
  updatedAt: number;
}

const KEY = ['admin', 'users'] as const;

export function useAdminUsers() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api<{ users: AdminUser[] }>('/api/admin/users'),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      email: string;
      nombre: string;
      rol: 'admin' | 'lector';
      password: string;
    }) => api<{ user: AdminUser }>('/api/admin/users', { method: 'POST', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Pick<AdminUser, 'nombre' | 'email' | 'rol' | 'activo'>>;
    }) => api<{ user: AdminUser }>(`/api/admin/users/${id}`, { method: 'PATCH', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string | null }) =>
      api<{ ok: true; tienePassword: boolean }>(`/api/admin/users/${id}/password`, {
        method: 'POST',
        body: { password },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useChangeMyPassword() {
  return useMutation({
    mutationFn: (data: { currentPassword?: string; newPassword: string }) =>
      api<{ ok: true }>('/api/auth/change-password', { method: 'POST', body: data }),
  });
}
